import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { convertHEIC } from "@tellescope/utils";

const fetchConvertedHEIC = async (src: string): Promise<string | null> => {
  const response = await fetch(src);
  if (!response.ok) return null;

  const converted = await convertHEIC(new File([await response.blob()], "preview.heic", { type: "image/heic" }));
  return converted ? URL.createObjectURL(converted) : null;
};

/**
 * Resolves a renderable image URL for a remote HEIC/HEIF file, which browsers cannot display
 * natively — pointing an `<img>` at the original bytes only ever fires `onError`.
 *
 * Pass `isHEIC: false` (the common case) and `src` is handed back untouched: nothing is fetched and
 * the decoder is never loaded. Pass `isHEIC: true` and the bytes are fetched and decoded once per
 * `cacheKey`, then cached, so remounting a preview or rendering the same file twice costs nothing.
 *
 * Returns undefined while decoding is in flight and when it fails, so callers can show a placeholder
 * instead of a broken image.
 *
 * @param src - The image URL, typically a signed URL from `useSecureFileUrl`
 * @param isHEIC - Whether `src` points at HEIC/HEIF bytes, e.g. from the file record's `type`
 * @param cacheKey - Stable identity for the file, e.g. its `secureName`. A signed `src` expires and
 *   is refetched under a new URL every few minutes; keying the decode on that would re-decode the
 *   same photo on every rotation and strand each previous blob. Defaults to `src` when the caller
 *   has no stable identifier.
 *
 * @example
 * ```tsx
 * const url = useSecureFileUrl(secureName);
 * const previewUrl = useHEICPreviewUrl(url, type === "image/heic", secureName);
 * return previewUrl ? <img src={previewUrl} alt={name} /> : <Placeholder />;
 * ```
 */
export const useHEICPreviewUrl = (
  src: string | undefined,
  isHEIC: boolean,
  cacheKey: string | undefined = src
): string | undefined => {
  const queryClient = useQueryClient();

  const { data: convertedUrl } = useQuery({
    queryKey: ["heic-preview", cacheKey],
    queryFn: () => fetchConvertedHEIC(src ?? ""),
    enabled: !!src && isHEIC,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  // The decoded blob lives in the browser's object-URL registry, outside React and outside the
  // query cache, so it has to be released by hand when the cache entry holding its only reference
  // goes away.
  useEffect(() => {
    if (!isHEIC || !cacheKey) return;

    return () => {
      const cached = queryClient.getQueryData<string | null>(["heic-preview", cacheKey]);
      if (!cached) return;
      if (
        queryClient
          .getQueryCache()
          .find({ queryKey: ["heic-preview", cacheKey] })
          ?.getObserversCount()
      )
        return;

      URL.revokeObjectURL(cached);
      queryClient.removeQueries({ queryKey: ["heic-preview", cacheKey], exact: true });
    };
  }, [queryClient, cacheKey, isHEIC]);

  return isHEIC ? convertedUrl ?? undefined : src;
};
